/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import { Element } from './Element';
import { ElementBox } from './ElementBox';
import { ElementCircle } from './ElementCircle';
import { levels } from './Levels';
import { Route } from './Route';
import { Stage, ObjectManipulator, SequenceActor, ActorDesc } from '@sick-davinci/glow';
import { DrawHelper } from './DrawHelper';
import { Vector2 } from 'three';

export class Game {
  private _currentLevel: number = 0;
  private _route: Route;
  private _plank: any;
  private _board: DrawHelper;
  private _drawRoute = false;

  constructor(private _stage: Stage, private _len: number, level: number = 0) {
    this._currentLevel = level || 0;
    this._plank = {
      type: 'box',
      from: [0, 0],
      to: [0, _len],
      width: 5,
      specialty: 'solution',
    };
    this._board = new DrawHelper(this._stage, 10, 2, { time: { value: 1.0 } });
  }

  setLevel(level: number) {
    this._currentLevel = level;
  }

  enableRoute(enable: boolean) {
    this._drawRoute = enable;

    if (!this._drawRoute) {
      const points = this._route.getPoints();
      while (points.length > 0) { points.pop(); }
    } else {
      this.calculateBallRoute();
    }
    this._board.createRoute(this._route);
  }

  calculateBallRoute (): boolean {
    const level = levels[this._currentLevel];

    // add walls and pillars
    const elements = this._preCalculateLevel();

    // add the plank
    elements.push(new ElementBox(this._plank, level.ball.radius));

    this._route = new Route(level.ball.endtime, level.ball.enddistance, level.goal);

    const point = new Vector2(level.ball.start[0], level.ball.start[1]);
    const dir = new Vector2(level.ball.direction[0], level.ball.direction[1]).normalize();

    return this._route.calculate(point, dir, level.ball.radius, elements);
  }

  drawRawLevel () {
    const level = levels[this._currentLevel];

    for (const elem of level.elements) {
      if (elem.type === 'box') {
        const e = elem as any;
        this._board.width = e.width;
        this._board.createWall(e.from[0], e.from[1], e.to[0], e.to[1]);
      } else if (elem.type === 'circle') {
        const e = elem as any;
        this._board.width = e.radius * 2;
        this._board.createPillar(e.center[0], e.center[1]);
      }
    }

    // plank
    this._board.width = this._plank.width;
    this._board.createPlank(this._plank.from[0], this._plank.from[1], this._plank.to[0], this._plank.to[1]);

    this._board.createBall(level.ball.start[0], level.ball.start[1], level.ball.radius);
    this._board.createDirection(level.ball.start[0], level.ball.start[1], level.ball.direction[0], level.ball.direction[1], level.ball.radius);
    this._board.createGoal(level.goal.position[0], level.goal.position[1], level.goal.radius);
    this._board.createGround(level.ground[0], level.ground[1]);
    if (this._drawRoute) {
       this.calculateBallRoute();
       this._board.createRoute(this._route);
    }
  }

  updatePlank (x: number, y: number, angle: number) {

    this._stage.applyThreeDirect((scene) => {
      const obj = scene.getObjectById(this._stage.getObjectIdByName('plank'));
      ObjectManipulator.setAttributeList(obj,
        [
          { name: 'position', value: [x, 0, y], type: 'vector' },
          { name: 'rotation', value: [0, angle, 0], type: 'vector' },
        ],
      );
    });

    const xv = Math.sin(angle);
    const yv = Math.cos(angle);

    this._plank = {
      type: 'box',
      from: [x + xv * this._len / 2, y + yv * this._len / 2],
      to: [x - xv * this._len / 2, y - yv * this._len / 2],
      width: 5,
      specialty: 'solution',
    };

    if (this._drawRoute) {
      this.calculateBallRoute();
      this._board.createRoute(this._route);
    }
  }

  startAnimation (): boolean {
    const result = this.calculateBallRoute();

    this._stage.applyThreeDirect((scene) => {
      const payload: ActorDesc = {
        objectId: this._stage.getObjectIdByName('ball'),
        act: 'sequence', // |move|set|stored",
        action: {
          name: 'ballMove',
          duration: this._route.getTimes()[-1],
          tracks:
            [
              { name: '.position', times: this._route.getTimes(), values: this._route.getKeys(), type: 'vector' },
            ],
        },
      };
      const actorSeq = new SequenceActor(scene, payload);
      scene.getStage().addActor(actorSeq);
      actorSeq.start();
    });

    return result;
  }

  private _preCalculateLevel (): Array<Element> {
    const elements: Array<Element> = new Array<Element>();

    const level = levels[this._currentLevel];

    for (const elem of level.elements) {
      if (elem.type === 'box') {
        elements.push(new ElementBox(elem, level.ball.radius));
      } else if (elem.type === 'circle') {
        elements.push(new ElementCircle(elem, level.ball.radius));
      }
    }
    return elements;
  }
}
